import { useAppSelector, useAppDispatch } from "@/services/hooks";
import { useCurrentPermission } from "@/services/userServices/useCurrentPermission";
import React, { useEffect, useReducer, useState } from "react";


import {
  addProduct,
  clearMessage,
} from "@/services/productServices/productActions";
import {
  getClients,
  getClientsByCountryCode,
} from "@/services/clientService/clientActions";
import { useImageUpload } from "@/services/ImageServices/useImageUpload";

import NotificationAlertOptions from "@/layouts/Alerts";
import { markInvalidFields } from "@/validation/markInvalidFields";

import AddGlobalProductView from "./AddGlobalProductView";
import type { AddGlobalProductValidation, ClientOption, PickedImage } from "./AddGlobalProductView";
import { clientOptions, productForm } from "./productForm";
import { asCaught } from "@/services/caughtError";

const formActions = productForm.actions;

const AddGlobalProductContainer = () => {
  const dispatch = useAppDispatch();
  const uploadImage = useImageUpload();

  // SCRUM-771: per-slice selectors (object-literal selector re-rendered on every dispatch).
  const products = useAppSelector((s) => s.products);
  const errors = useAppSelector((s) => s.errors);
  const permission = useCurrentPermission();
  const clients = useAppSelector((s) => s.clients.clientsCountryCode);
  const countryCode = useAppSelector((s) => s.sites?.site?.country_code);


  // SCRUM-2263: the draft (title, image, flags, rating, client) is one createSlice,
  // mounted locally; the client options are derived from the store in render.
  const [form, dispatchForm] = useReducer(productForm.reducer, productForm.getInitialState());
  const { title, productImg, sponsored, rating, selectedClient } = form;
  const selectedClientId = selectedClient.value || null;
  const clientsData = clientOptions(clients, "last");

  const [loading, setLoading] = useState(false);

  
  const [validation, setValidation] = useState<AddGlobalProductValidation>({
    title: true,
    alt_pic: true,
    productImg: true,
    country_code: true,
    client: true,
  });

  
  // SCRUM-754: countriesOptions removed — products are country-scoped (the country
  // comes from the current site), so the add form needs no country picker.

  
  useEffect(() => {
    if (countryCode) {
      dispatch(getClientsByCountryCode(countryCode));
    }
  }, [dispatch, countryCode]);


  // Server errors: stop the spinner and mark the invalid fields. Adjusting state while rendering
  // (React: "storing information from previous renders") instead of an effect that only
  // copies the store value into state (S9 mirror-effect, SCRUM-2254).
  const [seenErrors, setSeenErrors] = useState<typeof errors | null>(null);
  if (errors !== seenErrors) {
    setSeenErrors(errors);
    if (errors?.errors) {
      setValidation(markInvalidFields(validation, errors.errors));
      setLoading(false);
    }
  }

  // Success message -> toast + reset fields
  useEffect(() => {
    const msg = products?.message;
    if (msg) {
      NotificationAlertOptions("success", msg);
      dispatch(clearMessage());
      
      dispatchForm(formActions.saved());
      setLoading(false);
    }
  }, [products?.message, dispatch]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatchForm(formActions.titleChanged(e.target.value));
    setValidation((prev) => ({ ...prev, title: true }));
  };

  const handleRatingChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    dispatchForm(formActions.ratingChanged(e.target.value));

  const handleToggleSponsored = (e: React.ChangeEvent<HTMLInputElement>) =>
    dispatchForm(formActions.sponsoredToggled(e.target.checked));

  const handleSelectClient = (opt: ClientOption | null) => dispatchForm(formActions.clientSelected(opt));

  const handleReceiveImage = (img: PickedImage | null) => dispatchForm(formActions.imageReceived(img));

  const handleAdd = async (e: React.MouseEvent) => {
    e.preventDefault();

    // Build product object (country_code comes from site)
    const product = {
      country_code: countryCode,
      title,
      alt_pic: title,
      // SCRUM-312: `active` is deliberately absent, so a new product takes the
      // schema default (`true`). The form used to send its own state, which
      // reset to `false` after every successful add — so the second product
      // added in one session was created inactive unless the editor noticed.
      rating,
      sponsored,
      ...(selectedClientId ? { client: selectedClientId } : {}),
    };

    if (productImg?.data) {
      const path = `/${countryCode}/product/${productImg.imgName}`;
      setLoading(true);
      // Linearised (SCRUM-911): upload, then save inline with the returned URL,
      // instead of stashing pendingProduct and saving from a state.images.url
      // watch-effect. The old effect-based flow left `loading` stuck with no
      // toast on an upload failure (QUOTE_FAILED routes nowhere); surface it.
      try {
        const url = await uploadImage(productImg.data, path);
        dispatch(addProduct({ ...product, productImg: url }));
      } catch (err) {
        setLoading(false);
        NotificationAlertOptions(
          "error",
          asCaught(err)?.response?.data?.message ||
            asCaught(err)?.message ||
            "Saving the product failed. Please try again."
        );
      }
      return;
    }

    
    if (productImg?.secure_url) {
      dispatch(addProduct({ ...product, productImg: productImg.secure_url }));
      return;
    }


    dispatch(addProduct(product));
  };

  const canUseForm = !!(permission?.publish || permission?.edit);

  return (
    <AddGlobalProductView
      
      canUseForm={canUseForm}

      // values
      title={title}
      sponsored={sponsored}
      rating={rating}
      selectedClient={selectedClient}
      clientsData={clientsData}
      loading={loading}
      validation={validation}

      // handlers
      onTitleChange={handleTitleChange}
      onRatingChange={handleRatingChange}
      onToggleSponsored={handleToggleSponsored}
      onSelectClient={handleSelectClient}
      onReceiveImage={handleReceiveImage}
      onAdd={handleAdd}
    />
  );
};

export default AddGlobalProductContainer;
